import { LegalShell } from "@/components/legal/LegalShell";

const LAST_UPDATED = "July 19, 2026";

const SECTIONS = [
  {
    id: "what-beta-means",
    title: "What being on the beta means",
    body: (
      <>
        <p>
          Last Bookmark is currently in <strong>beta</strong>. That means the platform is a
          working product used by real authors and beta readers, but it is still evolving. Some
          features are still being built, some are being refined, and a few things may change,
          break, or move around while we improve based on your feedback.
        </p>
        <p>
          We're grateful you're here early. Your projects, applications, messages, and reviews
          are treated as real work — but the platform itself is a work in progress, and using it
          during the beta phase means accepting that occasional bumps come with the territory.
        </p>
      </>
    ),
  },
  {
    id: "what-you-can-do",
    title: "What you can do today",
    body: (
      <>
        <p>Right now, you can:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Create and edit your author or beta reader profile.</li>
          <li>Post projects (as an author) with genre, heat level, and word count.</li>
          <li>Apply to projects (as a beta reader) with a personal note.</li>
          <li>Message directly with the other party once an application or coffee chat is opened.</li>
          <li>Share manuscripts privately with a chosen reader.</li>
          <li>Leave a structured review at the end of a completed read-through.</li>
          <li>Apply for Certified Beta Reader status and earn a badge on your profile.</li>
        </ul>
      </>
    ),
  },
  {
    id: "payments-during-beta",
    title: "Payments during beta",
    body: (
      <>
        <p>
          <strong>Last Bookmark does not currently process, hold, or protect payments.</strong>{" "}
          There is no escrow, no invoicing, no chargeback protection, and no built-in payment
          rails during the beta phase.
        </p>
        <p>
          If you decide to exchange money with another user for a beta read, editorial pass, or
          related service, that arrangement is made <strong>directly between you and the other
          user, at your own discretion and risk</strong>. Last Bookmark is not a party to the
          agreement and does not guarantee that either side will perform. Last Bookmark also
          does not perform identity verification — an email address on file is only proof that
          the account has a working inbox; it does not confirm who the account holder is.
        </p>
        <p>
          Because we are not processing your payment, we cannot help resolve payment disputes,
          refund transactions, reverse chargebacks, or enforce contracts made between users
          during the beta. See our{" "}
          <a href="/payment-guidance" className="link-underline">Payment guidance</a> for
          practical tips on staying safe when you do choose to arrange payment with another
          user.
        </p>
        <p>
          Secure platform-managed payments through Stripe Connect are <strong>planned for a
          future update</strong>, but are not currently available. The exact payment flow,
          payout timing, protections, fees, and dispute process will be clearly disclosed
          inside the app before those features become available.
        </p>
        <p className="rounded-2xl border border-[hsl(var(--sb-forest))]/35 bg-[hsl(var(--sb-forest))]/5 p-4 text-sm leading-relaxed" data-testid="beta-terms-fee-notice">
          <strong className="uppercase tracking-[0.14em] text-[hsl(var(--sb-forest-2))] text-xs block mb-2">Payment and fee notice</strong>
          Last Bookmark is currently in beta. During this testing phase, users may arrange payment independently at their own risk. Once secure in-platform payments launch, completed paid projects will include a platform service fee. The exact fee will be clearly shown before either party agrees to payment.
        </p>
      </>
    ),
  },
  {
    id: "changes-and-availability",
    title: "Features may change; uptime is not guaranteed",
    body: (
      <>
        <p>
          Features may be added, adjusted, or removed as we iterate. We aim to be considerate
          with breaking changes and to notify you inside the app when something material
          shifts, but during beta we can't promise every feature you use today will exist in
          the same form tomorrow.
        </p>
        <p>
          Similarly, the platform may occasionally be unavailable for maintenance, upgrades, or
          due to bugs. We do our best to keep Last Bookmark healthy, but we don't guarantee
          uninterrupted access during the beta phase.
        </p>
      </>
    ),
  },
  {
    id: "accepting-these-terms",
    title: "Accepting these Beta Terms",
    body: (
      <>
        <p>
          Every user must accept the Beta Terms before they can use Last Bookmark. When you
          check the acknowledgment box in the sign-in modal and click Continue, you are
          confirming that you have read this notice and that you accept these terms.
        </p>
        <p>
          If we materially update the Beta Terms, we will re-prompt every user to accept the
          updated version on their next sign-in. You can always find the current version at{" "}
          <a href="/beta-terms" className="link-underline">lastbookmark.com/beta-terms</a>.
        </p>
      </>
    ),
  },
  {
    id: "relationship-to-other-docs",
    title: "How these Beta Terms fit with our other documents",
    body: (
      <>
        <p>
          These Beta Terms sit alongside our{" "}
          <a href="/terms" className="link-underline">Terms of Service</a>,{" "}
          <a href="/privacy" className="link-underline">Privacy Policy</a>, and{" "}
          <a href="/community-guidelines" className="link-underline">Community Guidelines</a>.
          Where the Beta Terms cover something specifically — like payments during the beta —
          the Beta Terms take precedence for now. Everything else is governed by the other
          documents.
        </p>
      </>
    ),
  },
  {
    id: "questions",
    title: "Questions",
    body: (
      <>
        <p>
          Reach the founding team at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          . We read every message and typically reply within one business day.
        </p>
      </>
    ),
  },
];

export default function BetaTerms() {
  return (
    <LegalShell
      eyebrow="Beta"
      title="Last Bookmark Beta Terms"
      intro="A plain-English explanation of what it means to use Last Bookmark while we're still in beta — including how payments work today and what we can (and can't) do to protect you."
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
      testid="beta-terms"
    />
  );
}
